import { type FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button, Card, CardContent, Stack, Typography } from "@mui/material";
import { useToken } from "@packsize/authentication";
import { DateTime } from "luxon";

import { useCopyToClipboard } from "@/hooks";

import { InfoRow } from "./info-row";

export const TokenInformationCard: FC = () => {
	const intl = useIntl();
	const { token, decodedToken } = useToken();
	const copyToClipboard = useCopyToClipboard();

	if (decodedToken === null) {
		return null;
	}

	const issuedAt = DateTime.fromSeconds(decodedToken.iat);
	const expiresAt = DateTime.fromSeconds(decodedToken.exp);
	const tokenLifetimeSeconds = expiresAt.diff(issuedAt, "seconds").seconds;
	const tokenLifetimeHours = expiresAt.diff(issuedAt, "hours").hours;
	const tokenExpiresInSeconds = expiresAt.diffNow("seconds").seconds;

	const handleCopyTokenClicked = () => {
		copyToClipboard(`${window.location.origin};${token}`);
	};

	return (
		<Card id="developer-token-information">
			<CardContent>
				<Typography variant="h3" component="h2" gutterBottom>
					<FormattedMessage id="Token Information" defaultMessage="Token Information" />
				</Typography>
				<Stack spacing={2}>
					<InfoRow
						label={intl.formatMessage({
							id: "Token Expiration (seconds)",
							defaultMessage: "Token Expiration (seconds)",
						})}
						value={tokenLifetimeSeconds}
					/>
					<InfoRow
						label={intl.formatMessage({
							id: "Token Expiration (hours)",
							defaultMessage: "Token Expiration (hours)",
						})}
						value={tokenLifetimeHours.toFixed(2)}
					/>
					<InfoRow
						label={intl.formatMessage({
							id: "Token Expires At",
							defaultMessage: "Token Expires At",
						})}
						value={expiresAt.toISO() ?? ""}
					/>
					<InfoRow
						label={intl.formatMessage({
							id: "Token Expires In (seconds)",
							defaultMessage: "Token Expires In (seconds)",
						})}
						value={Math.round(tokenExpiresInSeconds)}
					/>
					<Button
						size="large"
						variant="contained"
						onClick={handleCopyTokenClicked}
						data-pendoid="simulators__button__copy-token"
						sx={{ alignSelf: "flex-end" }}
					>
						<FormattedMessage id="Copy Token For Local Usage" defaultMessage="Copy Token For Local Usage" />
					</Button>
				</Stack>
			</CardContent>
		</Card>
	);
};
